import { useState, useMemo } from "react";
import {
  privateAgent,
  Button,
  DialogBox,
  TextButton,
  AlertDialog,
  useTitle,
} from "../../../lib";
import { DataTable } from "../../../lib/Forms/DataTable";
import { MRT_ColumnDef } from "material-react-table";
import { Formik, Form } from "formik";
import { useQuery, useQueryClient } from "react-query";
import { routesName } from "../../../lib/RoutesName/Routes";
import { Mode } from "../../../components/shared/dropdown/mode";
import { useSnackbar } from "notistack";
import ClassModeMenu from "../../../components/formFields/Classes/ClassModeMenu";
import { AddBehaviorCategoryProps } from "../../../lib/Types/BehaviorCategoryProps";
import AddBehaviorCategoryValidationSchema from "../../../components/formFields/Behaviors/AddBehaviorCategoryValidationSchema";
import { AddMeasurementProps } from "../../../lib/Types/MeasurementProps";
import {
  editMeasurementIntitialValues,
  measurementInitialValues,
} from "../../../components/formFields/Measurements/measurement-helpers";
import AddMeasurementFields from "../../../components/formFields/Measurements/AddMeasurementFields";
import {
  ButtonContainer,
  TopContainerNoMargin,
  UsersContainer,
  TableContainer,
} from "src/styles/Containers";
import { TitleLarge } from "src/styles/Typography";

function MeasurementPage() {
  useTitle("Measurements | Paige");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [openAddMeasurementDialog, setOpenAddMeasurementDialog] =
    useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [measurementId, setMeasurementId] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [fieldInitialValues, setFieldInitialValues] =
    useState<AddMeasurementProps>(measurementInitialValues);
  const queryClient = useQueryClient();

  let { isLoading, data } = useQuery(["MeasurementQuery"], async () => {
    const { data } = await privateAgent.get(
      routesName.MeasurementRoute({}).get_measurement
    );
    if (data.status === 200) {
      const results = data.result.items;
      return results;
    }
  });

  const handleSubmit = async (values: AddBehaviorCategoryProps) => {
    try {
      if (isEdit) {
        const res = await privateAgent.put(
          routesName.MeasurementRoute({ id: measurementId }).update_measurement,
          values
        );

        if (res.data && res.data.status === 200) {
          queryClient.invalidateQueries("MeasurementQuery");
          setOpenAddMeasurementDialog(false);
          enqueueSnackbar("Measurement  edited successfully", {
            variant: "success",
          });
        }
      } else {
        const res = await privateAgent.post(
          routesName.MeasurementRoute({}).add_measurement,
          values
        );
        if (res.status === 201) {
          queryClient.invalidateQueries("MeasurementQuery");
          setOpenAddMeasurementDialog(false);
          enqueueSnackbar("Measurement  added successfully", {
            variant: "success",
          });
        }
      }
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.error ?? "", { variant: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      const res = await privateAgent.delete(
        routesName.MeasurementRoute({ id: measurementId }).delete_measurement
      );
      if (res.status === 200) {
        queryClient.invalidateQueries("MeasurementQuery");
        setOpenDeleteDialog(false);
        enqueueSnackbar("Measurement deleted successfully", {
          variant: "success",
        });
      }
    } catch (error: any) {
      enqueueSnackbar(error.response.data.error ?? "", { variant: "error" });
    }
  };

  //grid defn
  type Measurement = {
    id: number;
    name: string;
  };
  const columns = useMemo<MRT_ColumnDef<Measurement>[]>(
    () => [
      {
        accessorFn: (row) => `${row.name}`,
        id: "name",
        header: "Name",
        minSize: 150,
      },
      {
        id: "actions",
        header: "Actions",
        headerClassName: "header-row",
        flex: 1,
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableBodyCellProps: {
          align: "right",
          className: "action",
        },
        size: 50,
        enableResizing: false, //disable resizing for this column
        Cell: ({ cell }) => (
          <>
            <ClassModeMenu
              id={0}
              onItemClick={function (id: number, mode: Mode): void {
                if (mode == "edit") {
                  setOpenAddMeasurementDialog(true);
                  setFieldInitialValues(
                    editMeasurementIntitialValues(cell.row.original)
                  );
                  setMeasurementId(cell.row.original.id.toString());
                  setIsEdit(true);
                }
                if (mode == "duplicate") {
                  setOpenAddMeasurementDialog(true);
                  setFieldInitialValues(
                    editMeasurementIntitialValues(cell.row.original)
                  );
                  setMeasurementId(cell.row.original.id.toString());
                  setIsEdit(false);
                }
                if (mode === "delete") {
                  setMeasurementId(cell.row.original.id.toString());
                  setOpenDeleteDialog(true);
                }
              }}
            />
          </>
        ),
      },
    ],
    []
  );
  //end of grid defn

  const handleRowClick = (column: any, row: any) => {
    if (column.id === "name") {
      setOpenAddMeasurementDialog(true);
      setFieldInitialValues(editMeasurementIntitialValues(row.original));
      setMeasurementId(row.original.id.toString());
      setIsEdit(true);
    }
  };

  return (
    <>
      <AlertDialog
        variety='error'
        onConfirm={() => handleDelete()}
        onClose={() => setOpenDeleteDialog(false)}
        isOpen={openDeleteDialog}
        title={"Are you sure you want to delete this measurement?"}
        content={
          "Deleting this measurement will permanently erase any data associated with this measurement."
        }
        btnText={{ cancel: "Cancel", action: "Delete" }}
      />
      <DialogBox
        isOpen={openAddMeasurementDialog}
        onClose={() => setOpenAddMeasurementDialog(false)}
        title={isEdit ? "Edit Measurement" : "Add Measurement"}
        content={
          <Formik
            initialValues={fieldInitialValues}
            onSubmit={handleSubmit}
            validationSchema={AddBehaviorCategoryValidationSchema}
          >
            {(formikProps) => (
              <Form>
                <AddMeasurementFields />
                <ButtonContainer>
                  <TextButton
                    label={"Cancel"}
                    onClick={() => setOpenAddMeasurementDialog(false)}
                  ></TextButton>
                  <Button
                    label={"Save"}
                    type={"submit"}
                    disabled={!formikProps.isValid}
                  ></Button>
                </ButtonContainer>
              </Form>
            )}
          </Formik>
        }
      />
      <TopContainerNoMargin>
        <TitleLarge>Measurements</TitleLarge>
        <Button
          label={"+ Add Measurement"}
          onClick={() => {
            setOpenAddMeasurementDialog(true);
            setIsEdit(false);
            setFieldInitialValues(measurementInitialValues);
          }}
        ></Button>
      </TopContainerNoMargin>
      <UsersContainer>
        <TableContainer>
          {data && data.length > 0 && (
            <DataTable
              columns={columns}
              data={data}
              onCellClick={handleRowClick}
            />
          )}
        </TableContainer>
      </UsersContainer>
    </>
  );
}

export default MeasurementPage;
